import React, { useEffect, useMemo, useState } from "react";
import { api, imageUrl } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useDepartments } from "@/contexts/DepartmentsContext";
import DepartmentChips, { DepartmentBadge } from "@/components/DepartmentChips";
import MachineFormDialog from "@/components/MachineFormDialog";
import MachineServiceLogDialog from "@/components/MachineServiceLogDialog";
import TechContactCard from "@/components/TechContactCard";
import ManageMachineCategoriesDialog from "@/components/ManageMachineCategoriesDialog";
import ExportButtons from "@/components/ExportButtons";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  Plus,
  Edit3,
  Search,
  Settings2,
  ChevronDown,
  Layers,
  AlertCircle,
  CheckCircle2,
  XCircle,
  Wrench,
  PackageX,
  Cog,
  HardHat,
  PhoneCall,
  Printer,
  LayoutGrid,
  Table as TableIcon,
  Monitor,
  Boxes,
} from "lucide-react";
import { toast } from "sonner";
import QuickViewMachinery from "@/components/QuickViewMachinery";

const STATUS_META = {
  available: { label: "Available", icon: CheckCircle2, cls: "bg-[#E6F4EA] text-[#1F6F3E]" },
  in_use: { label: "In use", icon: CheckCircle2, cls: "bg-[#E7EEFA] text-[#1F4A99]" },
  needs_service_unconfirmed: {
    label: "Service due? (unconfirmed)",
    icon: AlertCircle,
    cls: "bg-[#FFF7DB] text-[#5B4500]",
  },
  needs_service: { label: "Needs service", icon: Wrench, cls: "bg-[#FFE9C2] text-[#7A4A00]" },
  broken_unconfirmed: {
    label: "Broken (unconfirmed)",
    icon: AlertCircle,
    cls: "bg-[#FFF7DB] text-[#5B4500]",
  },
  broken: { label: "Broken", icon: Wrench, cls: "bg-[#FCE4E4] text-[#8A1F1F]" },
  retired: { label: "Retired", icon: PackageX, cls: "bg-[#EEE] text-[#555]" },
};

const StatusBadge = ({ status }) => {
  const meta = STATUS_META[status] || STATUS_META.available;
  const Icon = meta.icon;
  return (
    <span
      data-testid={`machine-status-${status}`}
      className={`inline-flex items-center gap-1 text-[10px] font-bold tracking-wide px-2 py-0.5 rounded-full border border-black/30 ${meta.cls}`}
    >
      <Icon size={10} strokeWidth={2.5} /> {meta.label}
    </span>
  );
};

const Machinery = () => {
  const { isManager } = useAuth();
  const { departments } = useDepartments();
  const [rows, setRows] = useState([]);
  const [categories, setCategories] = useState([]);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState("");
  const [category, setCategory] = useState("all");
  const [dept, setDept] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [serviceFor, setServiceFor] = useState(null);
  const [contactTechFor, setContactTechFor] = useState(null);
  // After the giant "Call primary tech NOW" button fires the tel: link, we
  // hold the machine + tech pair for a follow-up "Log as a call-out?" prompt.
  const [callOutPrompt, setCallOutPrompt] = useState(null);
  // Preset for the service log dialog (kind + notes) so a confirmed call-out
  // auto-fills the entry.
  const [servicePreset, setServicePreset] = useState(null);
  const [formOpen, setFormOpen] = useState(false);
  const [manageCatsOpen, setManageCatsOpen] = useState(false);
  const [viewMode, setViewMode] = useState(
    () => localStorage.getItem("machinery-view-mode") || "cards",
  );
  const switchView = (mode) => {
    setViewMode(mode);
    localStorage.setItem("machinery-view-mode", mode);
  };

  const load = async () => {
    const [{ data: m }, { data: c }, { data: u }] = await Promise.all([
      api.get("/machines"),
      api.get("/machine-categories"),
      api.get("/users/public"),
    ]);
    setRows(m || []);
    setCategories(c || []);
    setUsers(u || []);
  };
  useEffect(() => {
    load();
  }, []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return rows.filter((a) => {
      if (category !== "all" && (a.category || "") !== category) return false;
      if (dept !== "all" && (a.department || "") !== dept) return false;
      if (statusFilter !== "all") {
        const st = a.status || "available";
        // "Needs service" and "Broken" filter chips are inclusive of the
        // unconfirmed variants — a machine flagged via the card button is
        // just as much "needing service" as one confirmed by a manager.
        // The dedicated "Service due? (unconfirmed)" / "Broken (unconfirmed)"
        // chips still surface only the unconfirmed rows for manager review.
        if (statusFilter === "needs_service") {
          if (st !== "needs_service" && st !== "needs_service_unconfirmed") return false;
        } else if (statusFilter === "broken") {
          if (st !== "broken" && st !== "broken_unconfirmed") return false;
        } else if (st !== statusFilter) {
          return false;
        }
      }
      if (!q) return true;
      return (
        (a.name || "").toLowerCase().includes(q) ||
        (a.serial || "").toLowerCase().includes(q) ||
        (a.manufacturer || "").toLowerCase().includes(q) ||
        (a.model || "").toLowerCase().includes(q) ||
        (a.category || "").toLowerCase().includes(q)
      );
    });
  }, [rows, search, category, dept, statusFilter]);

  const serviceHint = (m) => {
    if (!m.next_service_at) return null;
    const due = new Date(m.next_service_at);
    const now = new Date();
    const days = Math.round((due - now) / 86400000);
    if (days < 0) return { label: `Overdue ${-days}d`, danger: true };
    if (days <= 14) return { label: `Due in ${days}d`, warn: true };
    return { label: due.toLocaleDateString(), ok: true };
  };

  const flag = async (m, status) => {
    try {
      await api.put(`/machines/${m.id}`, { status });
      toast.success(
        status === "needs_service_unconfirmed"
          ? `Flagged "${m.name}" — service due`
          : `Flagged "${m.name}" as broken`,
      );
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not flag");
    }
  };
  const confirmBroken = async (m) => {
    try {
      await api.put(`/machines/${m.id}`, { status: "broken" });
      toast.success("Confirmed broken");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not confirm");
    }
  };

  // Move — convert this machine into a different register (Assets /
  // Inventory). Preserves manufacturer→brand, category, cost, department,
  // notes and photos; drops machine-only fields (service log, spares,
  // hours). Source record is deleted server-side, then we reload.
  const moveTo = async (m, targetKind) => {
    const label = targetKind === "asset" ? "Assets" : "Inventory";
    if (
      !window.confirm(
        `Move "${m.name}" into ${label}?\n\nThis will:\n • create a new record in ${label} with the same manufacturer/model/serial/cost/photos\n • delete the current Machinery record\n\nService history linked to this machine ID will not be moved.`,
      )
    )
      return;
    try {
      await api.post("/records/move", {
        source_kind: "machine",
        source_id: m.id,
        target_kind: targetKind,
      });
      toast.success(`Moved "${m.name}" to ${label}`);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not move record");
    }
  };
  const retire = async (m) => {
    if (!window.confirm(`Retire "${m.name}"?`)) return;
    try {
      await api.put(`/machines/${m.id}`, { status: "retired" });
      toast.success("Retired");
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not retire");
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8" data-testid="machinery-page">
      <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
        <div>
          <div className="label-eyebrow">Inventory</div>
          <h1 className="font-display text-4xl">Machinery</h1>
          <p className="text-sm text-[#607087] mt-1">
            Workshop machines — grinders, saws, presses, printers. Service intervals, linked spares, breakage log.
            <span className="block text-xs text-[#8B96A6] mt-0.5">
              Use Machinery when the item needs <b>scheduled servicing or consumable parts</b> (ink, blades, belts). For simple &quot;who has it&quot; tracking, use <b>Assets</b> instead — don&apos;t enter in both.
            </span>
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <a
            href="/machinery-add-print"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="machinery-print-add-btn"
            className="nb-btn bg-white px-3 py-4 flex items-center gap-2 no-underline text-sm font-bold"
            title="Print blank forms staff can fill in for machines found on the floor that aren't in the register yet"
          >
            <Printer size={14} strokeWidth={2.5} /> Print add-machine form
          </a>
          <ExportButtons
            entity="machinery"
            rows={rows}
            testidPrefix="machinery-export"
          />
          {isManager && (
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <button
                  data-testid="machinery-manage-lists-btn"
                  className="nb-btn bg-white px-4 py-4 flex items-center gap-2"
                >
                  <Settings2 size={18} strokeWidth={2.5} /> Manage lists
                  <ChevronDown size={14} strokeWidth={2.5} />
                </button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="border-2 border-black rounded-xl shadow-[4px_4px_0_#001740] bg-white min-w-[14rem] p-1.5"
              >
                <DropdownMenuItem
                  data-testid="machinery-manage-categories-btn"
                  onClick={() => setManageCatsOpen(true)}
                  className="flex items-center gap-2 px-3 py-2 text-sm font-semibold cursor-pointer rounded-md focus:bg-[#F1E7FA] focus:text-[#5B3A99]"
                >
                  <Layers size={16} strokeWidth={2.5} /> Machine categories
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {isManager && (
            <button
              data-testid="machinery-add-btn"
              onClick={() => {
                setEditing(null);
                setFormOpen(true);
              }}
              className="nb-btn nb-btn-primary px-4 py-4 flex items-center gap-2"
            >
              <Plus size={18} strokeWidth={2.5} /> Add machine
            </button>
          )}
        </div>
      </div>

      <div className="relative mb-4">
        <Search
          size={18}
          strokeWidth={2.5}
          className="absolute left-3 top-1/2 -translate-y-1/2 text-[#001740]/60"
        />
        <input
          data-testid="machinery-search-input"
          className="nb-input pl-10"
          placeholder="Search name, serial, manufacturer, model…"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
        />
      </div>

      <div className="mb-3">
        <DepartmentChips value={dept} onChange={setDept} testidPrefix="machinery-dept" />
      </div>

      {categories.length > 0 && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="machinery-cat-chips">
          {categories.map((c) => {
            const active = category === c.name;
            return (
              <button
                key={c.id}
                data-testid={`machinery-cat-${c.name}`}
                onClick={() => setCategory(active ? "all" : c.name)}
                className={`nb-btn px-3 py-1.5 text-sm transition-all ${
                  active ? "nb-btn-dark" : "bg-white"
                }`}
              >
                {c.name}
              </button>
            );
          })}
        </div>
      )}

      <div className="flex flex-wrap gap-2 mb-6" data-testid="machinery-status-chips">
        {[
          "all",
          "available",
          "in_use",
          "needs_service_unconfirmed",
          "needs_service",
          "broken_unconfirmed",
          "broken",
          "retired",
        ].map((s) => {
          const active = statusFilter === s;
          const label = s === "all" ? "All statuses" : STATUS_META[s]?.label || s;
          return (
            <button
              key={s}
              data-testid={`machinery-status-filter-${s}`}
              onClick={() => setStatusFilter(s)}
              className={`nb-btn px-3 py-1.5 text-xs transition-all ${
                active ? "nb-btn-dark" : "bg-white"
              }`}
            >
              {label}
            </button>
          );
        })}
        <div
          className="nb-card p-0.5 flex items-center gap-0.5 ml-auto"
          data-testid="machinery-view-toggle"
          role="group"
          aria-label="View mode"
        >
          <button
            data-testid="machinery-view-cards"
            onClick={() => switchView("cards")}
            className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 rounded-md transition-colors ${
              viewMode === "cards"
                ? "bg-[#001740] text-white"
                : "text-[#001740] hover:bg-[#F4F8FB]"
            }`}
          >
            <LayoutGrid size={12} strokeWidth={2.5} /> Cards
          </button>
          <button
            data-testid="machinery-view-table"
            onClick={() => switchView("table")}
            className={`px-3 py-1.5 text-xs font-bold flex items-center gap-1 rounded-md transition-colors ${
              viewMode === "table"
                ? "bg-[#001740] text-white"
                : "text-[#001740] hover:bg-[#F4F8FB]"
            }`}
          >
            <TableIcon size={12} strokeWidth={2.5} /> Quick View
          </button>
        </div>
      </div>

      {viewMode === "table" ? (
        <QuickViewMachinery
          items={filtered}
          categories={categories}
          onSaved={load}
        />
      ) : filtered.length === 0 ? (
        <div
          className="nb-card p-8 text-center text-sm text-[#607087]"
          data-testid="machinery-empty"
        >
          No machines match your filters. {isManager && "Click + Add machine to start tracking one."}
        </div>
      ) : (
        <div className="grid gap-4 sm:grid-cols-2 xl:grid-cols-3">
          {filtered.map((m) => {
            const svc = serviceHint(m);
            const isBroken = ["broken", "broken_unconfirmed"].includes(m.status);
            const flaggableSvc = ["available", "in_use"].includes(m.status);
            return (
              <div
                key={m.id}
                data-testid={`machine-card-${m.id}`}
                className="nb-card p-4 flex flex-col gap-3"
              >
                <div className="flex items-start gap-3">
                  {m.image_paths?.[0] ? (
                    <img
                      src={imageUrl(m.image_paths[0])}
                      alt=""
                      className="w-20 h-20 rounded-lg border-2 border-black object-cover shrink-0"
                    />
                  ) : (
                    <div className="w-20 h-20 rounded-lg border-2 border-black bg-[#F4F8FB] shrink-0 flex items-center justify-center text-[#001740]/30">
                      <Cog size={28} strokeWidth={2} />
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <div className="font-display text-lg leading-tight truncate">{m.name}</div>
                    <div className="text-xs text-[#607087] truncate">
                      {[m.manufacturer, m.model].filter(Boolean).join(" · ") || "—"}
                    </div>
                    <div className="flex flex-wrap items-center gap-1 mt-1">
                      <StatusBadge status={m.status || "available"} />
                      {m.category && (
                        <span className="label-eyebrow px-1.5 py-0.5 rounded border-2 border-black bg-[#F1E7FA] text-[#5B3A99]">
                          {m.category}
                        </span>
                      )}
                      <DepartmentBadge department={m.department} />
                    </div>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-x-3 gap-y-1 text-xs">
                  {m.serial && (
                    <div>
                      <div className="label-eyebrow text-[#607087]">Serial</div>
                      <div className="font-mono-num truncate">{m.serial}</div>
                    </div>
                  )}
                  {m.service_interval_days ? (
                    <div>
                      <div className="label-eyebrow text-[#607087]">Interval</div>
                      <div className="font-mono-num">{m.service_interval_days}d</div>
                    </div>
                  ) : null}
                  {m.last_serviced_at && (
                    <div>
                      <div className="label-eyebrow text-[#607087]">Last serviced</div>
                      <div className="font-mono-num">{m.last_serviced_at}</div>
                    </div>
                  )}
                  {m.has_hour_meter
                    ? m.hours != null && (
                        <div>
                          <div className="label-eyebrow text-[#607087]">Hours</div>
                          <div className="font-mono-num font-bold">{m.hours}</div>
                        </div>
                      )
                    : m.hours_estimated_now != null && (
                        <div data-testid={`machine-est-hours-${m.id}`}>
                          <div className="label-eyebrow text-[#607087]">Hours (est.)</div>
                          <div className="font-mono-num font-bold">≈ {m.hours_estimated_now}</div>
                        </div>
                      )}
                  {svc && (
                    <div>
                      <div className="label-eyebrow text-[#607087]">Next service</div>
                      <div
                        className={`font-semibold ${
                          svc.danger ? "text-[#8A1F1F]" : svc.warn ? "text-[#7A5500]" : ""
                        }`}
                      >
                        {svc.label}
                      </div>
                    </div>
                  )}
                  {m.spares?.length > 0 && (
                    <div className="col-span-2">
                      <div className="label-eyebrow text-[#607087]">Linked spares</div>
                      <div className="text-xs">{m.spares.length} item{m.spares.length === 1 ? "" : "s"}</div>
                    </div>
                  )}
                  {m.tech_links?.length > 0 && (
                    <div
                      className="col-span-2"
                      data-testid={`machine-tech-summary-${m.id}`}
                    >
                      <div className="label-eyebrow text-[#607087] flex items-center gap-1">
                        <HardHat size={11} strokeWidth={2.5} /> Service tech
                      </div>
                      <div className="text-xs">
                        {(() => {
                          const primary = m.tech_links.find((tl) => tl.is_primary);
                          const p = primary || m.tech_links[0];
                          const extra = m.tech_links.length - 1;
                          return (
                            <span>
                              {primary && (
                                <span className="text-[#7A4A00] mr-1">★</span>
                              )}
                              <span className="font-bold">{p.tech_name}</span>
                              {p.supplier_name && (
                                <span className="text-[#607087]">
                                  {" "}
                                  · {p.supplier_name}
                                </span>
                              )}
                              {extra > 0 && (
                                <span className="text-[#607087]"> +{extra} more</span>
                              )}
                            </span>
                          );
                        })()}
                      </div>
                    </div>
                  )}
                  {m.service_agreement_end && (() => {
                    const end = new Date(m.service_agreement_end);
                    const daysLeft = Math.floor((end - new Date()) / 86400000);
                    let bg = "#DFF7E4", fg = "#0B6B2A", label = `${daysLeft}d left`;
                    if (daysLeft < 0) { bg = "#E5263F"; fg = "#fff"; label = `Expired ${Math.abs(daysLeft)}d ago`; }
                    else if (daysLeft < 7) { bg = "#FCC8CE"; fg = "#8A1F1F"; label = `Renew — ${daysLeft}d`; }
                    else if (daysLeft < 30) { bg = "#FFE1CC"; fg = "#7A3E00"; label = `Renew — ${daysLeft}d`; }
                    else if (daysLeft < 60) { bg = "#FFF3D4"; fg = "#7A5A00"; label = `${daysLeft}d left`; }
                    return (
                      <div className="col-span-2" data-testid={`machine-agreement-chip-${m.id}`}>
                        <div className="label-eyebrow text-[#607087] flex items-center gap-1">
                          Service agreement
                        </div>
                        <span
                          className="inline-flex items-center px-2 py-0.5 rounded-full text-[10px] font-bold mt-0.5"
                          style={{ background: bg, color: fg }}
                        >
                          {label}
                          {m.service_agreement_supplier && (
                            <span className="ml-1 opacity-80">
                              · {m.service_agreement_supplier}
                            </span>
                          )}
                        </span>
                      </div>
                    );
                  })()}
                </div>

                {/* Emergency call-out: giant red button appears the second a
                    machine is flagged broken. One tap dials the primary tech
                    (or the first-listed one if none is starred) and a
                    follow-up prompt offers to log the call as a Call-out
                    service entry so downtime metrics stay accurate. */}
                {isBroken && m.tech_links?.length > 0 && (() => {
                  const primary =
                    m.tech_links.find((tl) => tl.is_primary) || m.tech_links[0];
                  return (
                    <a
                      data-testid={`machine-call-primary-${m.id}`}
                      href={primary.phone ? `tel:${primary.phone}` : undefined}
                      onClick={async (e) => {
                        // Fetch fresh phone from the technicians API in case
                        // the snapshot is missing (older machine rows).
                        let phone = primary.phone;
                        if (!phone) {
                          try {
                            const { data } = await api.get(
                              `/technicians/${primary.tech_id}`,
                            );
                            phone = data?.phone;
                            if (phone) {
                              window.location.href = `tel:${phone}`;
                            } else {
                              toast.error("No phone number on file for this tech");
                              e.preventDefault();
                              return;
                            }
                          } catch {
                            toast.error("Could not load tech phone");
                            e.preventDefault();
                            return;
                          }
                        }
                        // Show the follow-up prompt shortly after so the
                        // dialer opens first on iPad, then the confirm modal
                        // is ready when the user comes back.
                        setTimeout(
                          () =>
                            setCallOutPrompt({
                              machine: m,
                              tech: primary,
                              phone,
                            }),
                          300,
                        );
                      }}
                      className="nb-btn nb-btn-danger w-full py-4 flex items-center justify-center gap-3 text-lg font-black mb-2 animate-[pulse-danger_1.5s_ease-in-out_infinite]"
                      style={{
                        background: "#E5263F",
                        color: "#fff",
                        borderColor: "#000",
                      }}
                      title={`Dial ${primary.tech_name} now`}
                    >
                      <PhoneCall size={22} strokeWidth={2.5} />
                      <span className="tracking-wide uppercase">
                        Call {primary.tech_name?.split(" ")[0] || "tech"} NOW
                      </span>
                    </a>
                  );
                })()}

                <div className="flex flex-wrap gap-2 pt-2 border-t border-black/10">
                  {m.tech_links?.length > 0 && (
                    <button
                      data-testid={`machine-contact-tech-${m.id}`}
                      onClick={() => {
                        const primary =
                          m.tech_links.find((tl) => tl.is_primary) ||
                          m.tech_links[0];
                        setContactTechFor(primary.tech_id);
                      }}
                      className="nb-btn bg-[#FFF9EC] px-3 py-1.5 text-xs flex items-center gap-1"
                      title="Open primary service contact"
                    >
                      <HardHat size={12} strokeWidth={2.5} /> Contact tech
                    </button>
                  )}
                  {isManager && (
                    <button
                      data-testid={`machine-service-${m.id}`}
                      onClick={() => setServiceFor(m)}
                      className="nb-btn nb-btn-success px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <Wrench size={12} strokeWidth={2.5} /> Log service
                    </button>
                  )}
                  {isManager && (
                    <button
                      data-testid={`machine-edit-${m.id}`}
                      onClick={() => {
                        setEditing(m);
                        setFormOpen(true);
                      }}
                      className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <Edit3 size={12} strokeWidth={2.5} /> Edit
                    </button>
                  )}
                  {isManager && (
                    <button
                      data-testid={`machine-move-asset-${m.id}`}
                      onClick={() => moveTo(m, "asset")}
                      className="nb-btn bg-[#FFF7D6] px-3 py-1.5 text-xs flex items-center gap-1"
                      title="Convert this record into Assets (keeps manufacturer/model/serial/cost/photos)"
                    >
                      <Monitor size={12} strokeWidth={2.5} /> → Assets
                    </button>
                  )}
                  {isManager && (
                    <button
                      data-testid={`machine-move-stock-${m.id}`}
                      onClick={() => moveTo(m, "stock")}
                      className="nb-btn bg-[#FFF7D6] px-3 py-1.5 text-xs flex items-center gap-1"
                      title="Convert this record into Inventory (turns into a stock line)"
                    >
                      <Boxes size={12} strokeWidth={2.5} /> → Inventory
                    </button>
                  )}
                  {flaggableSvc && (
                    <button
                      data-testid={`machine-flag-service-${m.id}`}
                      onClick={() => flag(m, "needs_service_unconfirmed")}
                      className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <AlertCircle size={12} strokeWidth={2.5} /> Needs service
                    </button>
                  )}
                  {flaggableSvc && (
                    <button
                      data-testid={`machine-flag-broken-${m.id}`}
                      onClick={() => flag(m, "broken_unconfirmed")}
                      className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <XCircle size={12} strokeWidth={2.5} /> Broken
                    </button>
                  )}
                  {m.status === "broken_unconfirmed" && isManager && (
                    <button
                      data-testid={`machine-confirm-broken-${m.id}`}
                      onClick={() => confirmBroken(m)}
                      className="nb-btn nb-btn-danger px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <Wrench size={12} strokeWidth={2.5} /> Confirm broken
                    </button>
                  )}
                  {isManager && m.status !== "retired" && !isBroken && (
                    <button
                      data-testid={`machine-retire-${m.id}`}
                      onClick={() => retire(m)}
                      className="nb-btn bg-white px-3 py-1.5 text-xs flex items-center gap-1"
                    >
                      <PackageX size={12} strokeWidth={2.5} /> Retire
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      <MachineFormDialog
        open={formOpen}
        onClose={() => setFormOpen(false)}
        editing={editing}
        categories={categories}
        departments={departments}
        onSaved={load}
        onCategoriesChanged={load}
      />
      <MachineServiceLogDialog
        open={!!serviceFor}
        onClose={() => {
          setServiceFor(null);
          setServicePreset(null);
        }}
        machine={serviceFor}
        users={users}
        onSaved={load}
        presetKind={servicePreset?.kind}
        presetNotes={servicePreset?.notes}
      />
      <ManageMachineCategoriesDialog
        open={manageCatsOpen}
        onClose={() => setManageCatsOpen(false)}
        onChanged={load}
      />
      <TechContactCard
        open={!!contactTechFor}
        technicianId={contactTechFor}
        onClose={() => setContactTechFor(null)}
      />
      {/* Follow-up prompt after a "Call primary tech NOW" tap. */}
      {callOutPrompt && (
        <div
          className="fixed inset-0 bg-black/60 z-[70] flex items-center justify-center p-4"
          data-testid="call-out-prompt-overlay"
          onClick={() => setCallOutPrompt(null)}
        >
          <div
            className="nb-card bg-white max-w-md w-full"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="p-5 border-b-2 border-black">
              <div className="label-eyebrow flex items-center gap-1.5">
                <PhoneCall size={12} strokeWidth={2.5} /> Call placed
              </div>
              <h2 className="font-display text-2xl leading-tight mt-1">
                Log this as a call-out?
              </h2>
              <div className="text-sm text-[#607087] mt-1">
                {callOutPrompt.machine.name} · dialed{" "}
                <span className="font-bold">{callOutPrompt.tech.tech_name}</span>{" "}
                {callOutPrompt.phone && (
                  <span className="font-mono-num">({callOutPrompt.phone})</span>
                )}
              </div>
            </div>
            <div className="p-5 text-sm">
              {(() => {
                // Coverage cross-reference — if the machine has an active
                // service agreement (not expired) AND the tech dialed comes
                // from the same supplier, the call-out is almost certainly
                // included. Show a green banner so staff don't pay twice.
                const m = callOutPrompt.machine;
                const t = callOutPrompt.tech;
                const endStr = m.service_agreement_end;
                if (!endStr) return null;
                const end = new Date(endStr);
                if (Number.isNaN(end.getTime()) || end < new Date()) return null;
                const agSup = (m.service_agreement_supplier || "").toLowerCase();
                const techSup = (t.supplier_name || "").toLowerCase();
                const supplierMatch = agSup && techSup && agSup === techSup;
                return (
                  <div
                    data-testid="call-out-coverage-banner"
                    className="mb-3 border-2 rounded-lg p-3 flex items-start gap-2"
                    style={{
                      background: supplierMatch ? "#DFF7E4" : "#FFF9EC",
                      borderColor: supplierMatch ? "#0B6B2A" : "#7A4A00",
                    }}
                  >
                    <span
                      className="text-lg leading-none mt-0.5"
                      aria-hidden="true"
                    >
                      {supplierMatch ? "✅" : "📄"}
                    </span>
                    <div className="text-xs">
                      {supplierMatch ? (
                        <>
                          <div className="font-bold text-[#0B6B2A]">
                            Covered by your {m.service_agreement_supplier} agreement
                          </div>
                          <div className="mt-0.5">
                            {t.tech_name}&apos;s call-out is included —
                            don&apos;t get billed twice. Log it as Call-out
                            so it still shows in downtime metrics.
                          </div>
                        </>
                      ) : (
                        <>
                          <div className="font-bold text-[#7A4A00]">
                            This machine has an active service agreement
                          </div>
                          <div className="mt-0.5">
                            Under contract with{" "}
                            <span className="font-bold">
                              {m.service_agreement_supplier || "another supplier"}
                            </span>
                            . {t.supplier_name ? `${t.tech_name} is from ${t.supplier_name} — ` : ""}
                            check whether this call-out is covered before you
                            approve any invoice.
                          </div>
                        </>
                      )}
                    </div>
                  </div>
                );
              })()}
              Adding a Call-out entry keeps downtime metrics accurate and
              gives you an audit trail of who was contacted, when. You can
              add cost and notes on the next screen.
            </div>
            <div className="p-5 border-t-2 border-black flex items-center justify-end gap-2 flex-wrap">
              <button
                data-testid="call-out-later-btn"
                onClick={() => setCallOutPrompt(null)}
                className="nb-btn bg-white px-4 py-2 text-sm"
              >
                Not now
              </button>
              <button
                data-testid="call-out-log-btn"
                onClick={() => {
                  const t = callOutPrompt.tech;
                  const stamp = new Date().toLocaleString(undefined, {
                    year: "numeric",
                    month: "short",
                    day: "2-digit",
                    hour: "2-digit",
                    minute: "2-digit",
                  });
                  setServicePreset({
                    kind: "Call-out",
                    notes: `Call-out to ${t.tech_name}${
                      t.supplier_name ? " · " + t.supplier_name : ""
                    } · dialed ${stamp}${
                      callOutPrompt.phone ? " · " + callOutPrompt.phone : ""
                    }`,
                  });
                  setServiceFor(callOutPrompt.machine);
                  setCallOutPrompt(null);
                }}
                className="nb-btn nb-btn-primary px-4 py-2 flex items-center gap-2 text-sm"
              >
                <Wrench size={14} strokeWidth={2.5} /> Log call-out
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default Machinery;
